/**
 * Salary Slip PDF Generator — ใบจ่ายเงินเดือน
 * Opens in a new browser tab with HTML + window.print()
 * Uses Google Fonts Sarabun. Two slips per A4 page.
 */

export interface SalarySlipData {
  employee_code: string
  nickname?: string
  name_en: string
  name_th: string
  department: string
  position?: string
  bank_name?: string
  bank_account?: string
  company_tax_id: string
  national_id: string
  pay_date: string // DD/MM/YYYY
  period: string // "January 2026"

  // Income
  base_salary: number
  overtime: number
  bonus: number
  commission: number
  other_income: number
  total_income: number

  // Deductions
  absent_deduction: number
  leave_deduction: number
  late_deduction: number
  income_tax: number
  social_security: number
  other_deduction: number
  total_deduction: number

  net_pay: number

  // YTD
  ytd_income: number
  ytd_tax: number
  ytd_ss: number
}

function fmtAmt(n: number): string {
  if (n == null || isNaN(n)) return '0.00'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function renderSlip(slip: SalarySlipData): string {
  return `
<div class="slip">
  <!-- Navy header bar -->
  <div class="header-bar">
    <div class="company-name-en">MailBIT Asia Pacific Co., Ltd.</div>
    <div class="company-name-th">บริษัท เมลบิท เอเซีย แปซิฟิค จำกัด</div>
  </div>

  <div class="slip-body">
    <!-- Company address -->
    <div class="company-address">
      919/252 Jewelry Trade Center, 20th Floor, Silom Road, Bangkok 10500<br/>
      Tax ID: 0105549080337 &nbsp;&nbsp;|&nbsp;&nbsp; Govt. UID: 3032261302
    </div>

    <!-- Title bar -->
    <div class="title-bar">SALARY SLIP / ใบจ่ายเงินเดือน</div>

    <!-- Period info -->
    <div class="period-row">
      <span>Pay Date: <strong>${slip.pay_date}</strong></span>
      <span>Period: <strong>${slip.period}</strong></span>
    </div>

    <!-- Employee info -->
    <div class="info-section">
      <div class="info-row">
        <span class="info-label">Employee:</span>
        <span class="info-value">${slip.employee_code} ${slip.nickname ? slip.nickname + ' ' : ''}${slip.name_en}</span>
      </div>
      <div class="info-row">
        <span class="info-label">ชื่อ-สกุล:</span>
        <span class="info-value">${slip.name_th}</span>
        <span class="info-label" style="margin-left:20px;">ID:</span>
        <span class="info-value">${slip.national_id}</span>
      </div>
      <div class="info-row">
        <span class="info-label">Department:</span>
        <span class="info-value">${slip.department}</span>
        ${slip.position ? `<span class="info-label" style="margin-left:20px;">Position:</span><span class="info-value">${slip.position}</span>` : ''}
      </div>
      ${slip.bank_name || slip.bank_account ? `
      <div class="info-row">
        <span class="info-label">Bank:</span>
        <span class="info-value">${slip.bank_name || ''} ${slip.bank_account || ''}</span>
      </div>` : ''}
    </div>

    <!-- Income / Deductions table -->
    <table class="detail-table">
      <thead>
        <tr>
          <th class="col-left">INCOME / รายได้</th>
          <th class="col-amt">Amount</th>
          <th class="col-right">DEDUCTIONS / รายหัก</th>
          <th class="col-amt">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Salary / เงินเดือน</td>
          <td class="amt">${fmtAmt(slip.base_salary)}</td>
          <td>Absent / ขาดงาน</td>
          <td class="amt">${fmtAmt(slip.absent_deduction)}</td>
        </tr>
        <tr>
          <td>Overtime / ค่าล่วงเวลา</td>
          <td class="amt">${fmtAmt(slip.overtime)}</td>
          <td>Leave / ลางาน</td>
          <td class="amt">${fmtAmt(slip.leave_deduction)}</td>
        </tr>
        <tr>
          <td>Bonus / โบนัส</td>
          <td class="amt">${fmtAmt(slip.bonus)}</td>
          <td>Late / มาสาย</td>
          <td class="amt">${fmtAmt(slip.late_deduction)}</td>
        </tr>
        <tr>
          <td>Commission / ค่าคอมมิชชั่น</td>
          <td class="amt">${fmtAmt(slip.commission)}</td>
          <td>Income Tax / ภาษีเงินได้</td>
          <td class="amt">${fmtAmt(slip.income_tax)}</td>
        </tr>
        <tr>
          <td>Other / อื่น ๆ</td>
          <td class="amt">${fmtAmt(slip.other_income)}</td>
          <td>Social Sec. / ประกันสังคม</td>
          <td class="amt">${fmtAmt(slip.social_security)}</td>
        </tr>
        <tr>
          <td></td>
          <td></td>
          <td>Other / อื่น ๆ</td>
          <td class="amt">${fmtAmt(slip.other_deduction)}</td>
        </tr>
      </tbody>
      <tfoot>
        <tr class="total-row">
          <td><strong>TOTAL INCOME</strong></td>
          <td class="amt"><strong>${fmtAmt(slip.total_income)}</strong></td>
          <td><strong>TOTAL DEDUCTION</strong></td>
          <td class="amt"><strong>${fmtAmt(slip.total_deduction)}</strong></td>
        </tr>
      </tfoot>
    </table>

    <!-- NET PAY -->
    <div class="net-pay-bar">
      <span class="net-label">NET PAY / เงินสุทธิ:</span>
      <span class="net-amount">&#3647; ${fmtAmt(slip.net_pay)}</span>
    </div>

    <!-- YTD section -->
    <div class="ytd-section">
      <span class="ytd-item">YTD Income: ${fmtAmt(slip.ytd_income)}</span>
      <span class="ytd-item">YTD Tax: ${fmtAmt(slip.ytd_tax)}</span>
      <span class="ytd-item">YTD Social Sec.: ${fmtAmt(slip.ytd_ss)}</span>
    </div>
  </div>
</div>`
}

function buildHtml(slips: SalarySlipData[]): string {
  const slipHtmls = slips.map(renderSlip)

  // Group into pages of 2
  const pages: string[] = []
  for (let i = 0; i < slipHtmls.length; i += 2) {
    const pageSlips = slipHtmls.slice(i, i + 2)
    pages.push(`<div class="page">${pageSlips.join('\n<div class="slip-divider"></div>\n')}</div>`)
  }

  return `<!DOCTYPE html>
<html lang="th">
<head>
<meta charset="UTF-8">
<title>Salary Slips - ใบจ่ายเงินเดือน</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Sarabun:wght@300;400;600;700&display=swap');

@page {
  size: A4 portrait;
  margin: 8mm;
}

* { margin: 0; padding: 0; box-sizing: border-box; }

body {
  font-family: 'Sarabun', 'TH SarabunPSK', 'Tahoma', sans-serif;
  font-size: 12px;
  line-height: 1.3;
  color: #222;
  background: #f0f0f0;
}

/* Print bar */
.print-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #1a237e;
  color: #fff;
  padding: 8px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-family: 'Sarabun', sans-serif;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
}
.print-bar span { font-size: 14px; font-weight: 600; }
.print-bar button {
  padding: 6px 24px;
  background: #fff;
  color: #1a237e;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Sarabun', sans-serif;
}
.print-bar button:hover { background: #e8eaf6; }
.print-spacer { height: 48px; }

/* Page container */
.page {
  width: 210mm;
  min-height: 297mm;
  margin: 0 auto 10px auto;
  background: #fff;
  padding: 0;
  display: flex;
  flex-direction: column;
  page-break-after: always;
}
.page:last-child { page-break-after: auto; }

/* Slip divider */
.slip-divider {
  border-top: 1px dashed #999;
  margin: 0;
}

/* Each slip = half A4 */
.slip {
  height: 140.5mm; /* half of 281mm (A4 minus margins) */
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Navy header */
.header-bar {
  background: #1a237e;
  color: #fff;
  padding: 8px 16px 6px 16px;
}
.company-name-en {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.company-name-th {
  font-size: 12px;
  font-weight: 400;
  opacity: 0.9;
}

/* Slip body */
.slip-body {
  flex: 1;
  padding: 6px 16px 8px 16px;
  display: flex;
  flex-direction: column;
}

/* Company address */
.company-address {
  font-size: 10px;
  color: #555;
  margin-bottom: 4px;
  line-height: 1.4;
}

/* Title bar */
.title-bar {
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #1a237e;
  padding: 4px 0;
  border-top: 2px solid #1a237e;
  border-bottom: 2px solid #1a237e;
  margin-bottom: 6px;
  letter-spacing: 1px;
}

/* Period row */
.period-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
  font-size: 11.5px;
}

/* Employee info */
.info-section {
  margin-bottom: 6px;
  padding: 4px 8px;
  background: #f5f7ff;
  border-radius: 3px;
  border-left: 3px solid #1a237e;
}
.info-row {
  display: flex;
  gap: 4px;
  margin-bottom: 1px;
  font-size: 11px;
}
.info-label {
  font-weight: 600;
  color: #444;
  white-space: nowrap;
}
.info-value {
  color: #111;
}

/* Detail table */
.detail-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 6px;
  font-size: 11px;
  flex-shrink: 0;
}
.detail-table th {
  background: #e8eaf6;
  color: #1a237e;
  font-weight: 700;
  font-size: 10.5px;
  padding: 3px 6px;
  text-align: left;
  border: 1px solid #c5cae9;
}
.detail-table th.col-amt {
  text-align: right;
  width: 18%;
}
.detail-table th.col-left,
.detail-table th.col-right {
  width: 32%;
}
.detail-table td {
  padding: 2px 6px;
  border: 1px solid #e0e0e0;
  vertical-align: middle;
}
.detail-table td.amt {
  text-align: right;
  font-family: 'Sarabun', monospace;
  font-variant-numeric: tabular-nums;
}
.detail-table .total-row td {
  background: #f5f5f5;
  border-top: 2px solid #1a237e;
  font-size: 11px;
}

/* NET PAY */
.net-pay-bar {
  background: #1a237e;
  color: #fff;
  padding: 6px 12px;
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.net-label {
  font-size: 13px;
  font-weight: 600;
}
.net-amount {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.5px;
}

/* YTD */
.ytd-section {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #666;
  padding: 3px 6px;
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: 2px;
}
.ytd-item { white-space: nowrap; }

/* Print styles */
@media print {
  body { background: #fff; }
  .no-print { display: none !important; }
  .print-spacer { display: none !important; }
  .page {
    margin: 0;
    box-shadow: none;
    page-break-after: always;
  }
  .page:last-child { page-break-after: auto; }
  .slip { height: 140.5mm; }
}

/* Screen-only page shadow */
@media screen {
  .page {
    box-shadow: 0 2px 12px rgba(0,0,0,0.15);
    margin-bottom: 20px;
  }
}
</style>
</head>
<body>
<div class="print-bar no-print">
  <span>Salary Slips / ใบจ่ายเงินเดือน (${slips.length} slip${slips.length > 1 ? 's' : ''})</span>
  <button onclick="window.print()">Print / Save as PDF</button>
</div>
<div class="print-spacer no-print"></div>

${pages.join('\n')}

</body>
</html>`
}

/** Generate salary slips for multiple employees — opens one page with all slips (2 per A4 page) */
export function generateSalarySlips(slips: SalarySlipData[]): void {
  if (!slips.length) return
  const html = buildHtml(slips)
  const w = window.open('', '_blank')
  if (w) {
    w.document.write(html)
    w.document.close()
  }
}

/** Generate salary slip for a single employee */
export function generateSalarySlip(slip: SalarySlipData): void {
  generateSalarySlips([slip])
}
